import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import {
  usePagination,
  usePageOffset,
  DEFAULT_PAGE_SIZE,
} from "@/hooks/use-pagination";
import { useDialogSearchParam } from "@/hooks/use-dialog-search-param";
import { useFilterSearchParams } from "@/hooks/use-filter-search-params";
import { AdaptiveSheet } from "@/components/ui/adaptive-sheet";
import { Button } from "@/components/ui/button";
import { Fab } from "@/components/ui/fab";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { ModalHeader, ModalBody, ModalFooter } from "@/components/ui/modal";
import { PageTitle } from "@/contexts/page-title-context";
import { Value, EmptyState, ErrorState, Text } from "@/components/ui/text";
import { Card } from "@/components/ui/card";
import { Icon } from "@/components/ui/icons";
import { IconButton } from "@/components/ui/icon-button";
import { PageLoadingOverlay } from "@/components/ui/page-loading-overlay";
import {
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  Table,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ResizeHandle } from "@/components/ui/resize-handle";
import {
  ResizableTableShell,
  type ColumnDef,
} from "@/components/ui/resizable-table-shell";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { HStack } from "@/components/ui/h-stack";
import { StickyHeaderSlot } from "@/components/ui/sticky-header-slot";
import { Surface } from "@/components/ui/surface";
import { TableViewport } from "@/components/ui/table-viewport";
import { VStack } from "@/components/ui/v-stack";
import { DividedList } from "@/components/ui/divided-list";
import { useSizeClass } from "@/hooks/use-size-class";
import { lazyComponent } from "@/lib/lazy-component";
const LocationMap = lazyComponent(() =>
  import("@/components/ui/location-map").then((m) => ({
    default: m.LocationMap,
  })),
);
import { AddressModal } from "@/components/forms/address-modal";
import { useUserEntity } from "@/contexts/user-entity-context";
import { getAddresses, deleteAddress, type Address } from "@/lib/api/addresses";
import { getBrokerShippers } from "@/lib/api/broker-parties";
import { getCurrentUser } from "@/lib/api/auth";
import { fromIntlLanguage } from "@/lib/i18n/config";
import { getLocalizedName } from "@/lib/localization";
import { highlight } from "@/lib/highlight";
import { addressSearchableFields } from "@/lib/search/address";
import { useDeleteWithConfirm } from "@/hooks/use-delete-with-confirm";

function AddressesPage() {
  const { t: tAddresses } = useTranslation("addresses");
  const { t: tCommon } = useTranslation("common");
  const { t: tCommonPagination } = useTranslation("common.pagination");
  const { t: tLoads } = useTranslation("loads");
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const searchParams = useSearch({ strict: false }) as Record<
    string,
    string | undefined
  >;
  const queryClient = useQueryClient();
  const { entity: userEntity } = useUserEntity();
  const isBroker = userEntity?.type === "Broker";
  const shipperParam = searchParams.shipper_id ?? "";
  const { dialog, openDialog, closeDialog } = useDialogSearchParam();
  const { filters, setFilter } = useFilterSearchParams({ keys: ["search"] });
  const searchQuery = filters.search;
  const [localShipperId, setSelectedShipperId] = useState(shipperParam);
  const isShipperLocked = !!shipperParam;
  // When URL has shipper_id, it takes precedence; otherwise use local state
  const selectedShipperId = isShipperLocked ? shipperParam : localShipperId;

  const { offset } = usePageOffset();

  // FilterToolbar already debounces draft → URL commits at 300ms, so
  // searchQuery here is the committed (debounced) value.
  const q = useMemo(() => {
    const trimmed = searchQuery.trim();
    return trimmed.length >= 3 ? trimmed : undefined;
  }, [searchQuery]);

  const addressQueryParams = useMemo(
    () => ({
      limit: DEFAULT_PAGE_SIZE,
      offset,
      shipper_id: isBroker && selectedShipperId ? selectedShipperId : undefined,
      q,
    }),
    [isBroker, selectedShipperId, offset, q],
  );

  const addressesQuery = useQuery({
    queryKey: queryKeys.addresses(addressQueryParams),
    queryFn: () => getAddresses(addressQueryParams),
  });

  const addressesData = addressesQuery.data;
  const addresses = useMemo(() => addressesData?.data ?? [], [addressesData]);
  const total = addressesData?.total ?? 0;
  const loading = addressesQuery.isLoading;
  const error = addressesQuery.isError ? "Failed to load addresses" : null;

  const { page, totalPages, setPage } = usePagination({ total });

  const brokerShippersQuery = useQuery({
    queryKey: ["broker-shippers", { limit: 100 }],
    queryFn: () => getBrokerShippers({ limit: 100 }),
    enabled: isBroker,
  });
  const shippersData = brokerShippersQuery.data;
  const brokerShippers = useMemo(
    () => shippersData?.data ?? [],
    [shippersData],
  );

  const invalidateAddresses = () => {
    void queryClient.invalidateQueries({ queryKey: ["addresses"] });
  };

  const {
    showConfirm: showDeleteConfirm,
    loading: deleteLoading,
    requestDelete,
    confirmDelete,
    cancelDelete,
  } = useDeleteWithConfirm({
    deleteFn: deleteAddress,
    onSuccess: invalidateAddresses,
    successMessage: tCommon("deleteSuccess"),
    errorMessage: tCommon("deleteFailed"),
  });

  const selectedBrokerShipper = useMemo(
    () => brokerShippers.find((shipper) => shipper.id === selectedShipperId),
    [brokerShippers, selectedShipperId],
  );

  // Search narrows server-side via ?q= — no client filter.
  const filteredAddresses = addresses;

  // Derive selected address from dialog ID — close stale dialog if entity not in current page
  const dialogId = dialog?.id ?? null;
  const selectedAddress = useMemo(
    () =>
      dialogId ? (addresses.find((a) => a.id === dialogId) ?? null) : null,
    [dialogId, addresses],
  );
  useEffect(() => {
    if (dialogId && !loading && !selectedAddress) {
      closeDialog();
    }
  }, [dialogId, loading, addresses.length, selectedAddress, closeDialog]);

  // Handle view in map click
  const handleViewInMap = (addr: Address) => {
    openDialog("map", addr.id);
  };

  // Handle edit click
  const handleEdit = (addr: Address) => {
    openDialog("address", addr.id);
  };

  // Handle add new
  const handleAddNew = () => {
    if (!isShipperLocked) {
      setSelectedShipperId("");
    }
    openDialog("address");
  };

  // Close modal
  const closeModal = () => {
    closeDialog();
  };

  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";

  // ─── Resizable columns ──────────────────────────────────────────────
  const userId = getCurrentUser()?.id ?? null;
  const columnDefs: ColumnDef[] = [
    { key: "label", defaultWidth: 200, minWidth: 100 },
    { key: "location", defaultWidth: 300, minWidth: 150 },
    { key: "actions", defaultWidth: 160, minWidth: 160 },
  ];

  return (
    <VStack gap="lg" data-testid="addresses-page">
      {/* Page header */}
      {/* addresses-header */}
      <PageTitle>{tAddresses("title")}</PageTitle>
      <StickyHeaderSlot>
        <Card
          data-testid="addresses-header-actions-card"
          variant="bordered"
          padding="xs"
        >
          <FilterToolbar
            data-testid="addresses-header-actions"
            searchPlaceholder={tAddresses("searchPlaceholder")}
            searchValue={searchQuery}
            onSearchChange={(v) => setFilter("search", v)}
            createAction={{
              label: tAddresses("addAddress"),
              onClick: handleAddNew,
              testId: "addresses-add-btn",
            }}
            pagination={{
              page,
              totalPages,
              perPage: DEFAULT_PAGE_SIZE,
              onPageChange: setPage,
              loading,
            }}
          />
        </Card>
      </StickyHeaderSlot>

      {/* Table */}
      <Card data-testid="addresses-content-card" padding="none">
        <TableViewport position="relative">
          {/* Loading state */}
          {loading && addresses.length === 0 && (
            <Table data-testid="addresses-table-skeleton">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{tAddresses("label")}</TableHeaderCell>
                  <TableHeaderCell>{tAddresses("location")}</TableHeaderCell>
                  <TableHeaderCell>{tLoads("actions")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton className="h-5 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="mb-2 h-4 w-40" />
                      <Skeleton className="h-3 w-56" />
                    </TableCell>
                    <TableCell>
                      <HStack align="stretch">
                        <Skeleton className="h-8 w-24" />
                        <Skeleton rounded="full" className="h-8 w-8" />
                      </HStack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Error state */}
          {error && !loading && (
            <ErrorState
              title={tCommon("errorLoadingData")}
              description={error}
              action={
                <Button
                  color="neutral"
                  variant="outline"
                  onClick={() => void addressesQuery.refetch()}
                >
                  {tCommon("retry")}
                </Button>
              }
              className="py-16"
            />
          )}

          {/* Table or Empty state */}
          {!loading && !error && filteredAddresses.length === 0 && (
            <EmptyState
              title={tAddresses("noAddressesTitle")}
              description={tAddresses("noAddressesDescription")}
              icon={<Icon.Location size="xl" />}
              action={
                !isCompact ? (
                  <Button size="md" onClick={handleAddNew}>
                    <Icon.Plus />
                    {tAddresses("addAddress")}
                  </Button>
                ) : undefined
              }
              className="py-16"
            />
          )}

          {loading && addresses.length > 0 && (
            <PageLoadingOverlay
              page={page}
              label={tCommonPagination("loadingPage")}
            />
          )}

          {!loading &&
            !error &&
            filteredAddresses.length > 0 &&
            (isCompact ? (
              <DividedList data-testid="addresses-card-list">
                {filteredAddresses.map((addr) => {
                  const fields = addressSearchableFields(addr, language);
                  return (
                    <VStack
                      gap="xs"
                      key={addr.id}
                      className="px-4 py-3"
                      data-testid={`address-row-${addr.id}`}
                    >
                      <HStack align="start" justify="between" gap="md">
                        <Text weight="semibold">
                          {highlight(fields.name, searchQuery)}
                        </Text>
                        <HStack gap="xs" shrink>
                          <IconButton
                            data-testid={`address-map-${addr.id}`}
                            variant="ghost"
                            size="sm"
                            title={tAddresses("viewInMap")}
                            onClick={() => handleViewInMap(addr)}
                          >
                            <Icon.Map size="sm" />
                          </IconButton>
                          <IconButton
                            data-testid={`address-edit-${addr.id}`}
                            variant="ghost"
                            size="sm"
                            title={tCommon("edit")}
                            onClick={() => handleEdit(addr)}
                          >
                            <Icon.Edit size="sm" />
                          </IconButton>
                          <IconButton
                            data-testid={`address-delete-${addr.id}`}
                            variant="ghost"
                            size="sm"
                            className="hover:text-[color:var(--error)]"
                            title={tCommon("delete")}
                            onClick={() => requestDelete(addr.id)}
                          >
                            <Icon.Trash size="sm" />
                          </IconButton>
                        </HStack>
                      </HStack>
                      <HStack align="start" gap="sm">
                        <Icon.Location
                          size="sm"
                          color="muted"
                          className="mt-0.5 flex-shrink-0"
                        />
                        <VStack gap="none">
                          <Text block>
                            {highlight(fields.city, searchQuery)},{" "}
                            {highlight(fields.country, searchQuery)}
                          </Text>
                          <Text size="xs" color="muted" block>
                            {highlight(fields.address, searchQuery)}
                          </Text>
                        </VStack>
                      </HStack>
                    </VStack>
                  );
                })}
              </DividedList>
            ) : (
              <ResizableTableShell
                tableId="addresses"
                userId={userId}
                columns={columnDefs}
                data-testid="addresses-table"
              >
                {({ onResizeStart }) => (
                  <>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell className="relative">
                          {tAddresses("label")}
                          <ResizeHandle
                            columnKey="label"
                            onResizeStart={onResizeStart}
                          />
                        </TableHeaderCell>
                        <TableHeaderCell className="relative">
                          {tAddresses("location")}
                          <ResizeHandle
                            columnKey="location"
                            onResizeStart={onResizeStart}
                          />
                        </TableHeaderCell>
                        <TableHeaderCell className="relative" sticky>
                          {tLoads("actions")}
                        </TableHeaderCell>
                        <TableHeaderCell className="w-auto" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAddresses.map((addr) => {
                        const fields = addressSearchableFields(addr, language);
                        return (
                          <TableRow
                            key={addr.id}
                            hoverable
                            data-testid={`address-row-${addr.id}`}
                          >
                            <TableCell>
                              <VStack gap="xs">
                                <Text weight="semibold">
                                  {highlight(fields.name, searchQuery)}
                                </Text>
                              </VStack>
                            </TableCell>
                            <TableCell>
                              <HStack align="start">
                                <Icon.Location
                                  size="sm"
                                  color="muted"
                                  className="mt-0.5"
                                />
                                <VStack gap="none">
                                  <Text block>
                                    {highlight(fields.city, searchQuery)},{" "}
                                    {highlight(fields.country, searchQuery)}
                                  </Text>
                                  <Text size="xs" color="muted" block>
                                    {highlight(fields.address, searchQuery)}
                                  </Text>
                                </VStack>
                              </HStack>
                            </TableCell>
                            <TableCell sticky>
                              <HStack className="whitespace-nowrap">
                                <Button
                                  data-testid={`address-map-${addr.id}`}
                                  color="neutral"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewInMap(addr)}
                                >
                                  <Icon.Map size="sm" />
                                  {tAddresses("viewInMap")}
                                </Button>
                                <IconButton
                                  data-testid={`address-edit-${addr.id}`}
                                  variant="ghost"
                                  size="sm"
                                  title={tCommon("edit")}
                                  onClick={() => handleEdit(addr)}
                                >
                                  <Icon.Edit size="sm" />
                                </IconButton>
                                <IconButton
                                  data-testid={`address-delete-${addr.id}`}
                                  variant="ghost"
                                  size="sm"
                                  className="hover:text-[color:var(--error)]"
                                  title={tCommon("delete")}
                                  onClick={() => requestDelete(addr.id)}
                                >
                                  <Icon.Trash size="sm" />
                                </IconButton>
                              </HStack>
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </>
                )}
              </ResizableTableShell>
            ))}
        </TableViewport>
      </Card>

      {/* Map Modal (AdaptiveSheet on compact, modal on regular/expanded) */}
      <AdaptiveSheet
        open={dialog?.type === "map" && !!selectedAddress}
        onDismiss={closeModal}
        size="xl"
      >
        {selectedAddress && (
          <>
            <ModalHeader>
              <Value size="lg" weight="semibold">
                {selectedAddress.name}
              </Value>
              <IconButton variant="ghost" size="sm" onClick={closeModal}>
                <Icon.X />
              </IconButton>
            </ModalHeader>
            <ModalBody className="p-0">
              <VStack className={isCompact ? "h-[250px]" : "h-[500px]"}>
                <LocationMap
                  latitude={selectedAddress.latitude}
                  longitude={selectedAddress.longitude}
                  name={selectedAddress.name}
                  address={selectedAddress.address}
                />
              </VStack>
              <Surface border="top" className="p-4">
                <HStack>
                  <Icon.Location size="sm" />
                  <Text>{selectedAddress.address}</Text>
                </HStack>
                <Text
                  size="xs"
                  color="muted"
                  className="mt-2 flex items-center gap-2"
                >
                  {getLocalizedName(selectedAddress.city, language)},{" "}
                  {getLocalizedName(selectedAddress.country, language)}
                </Text>
                <Text size="xs" color="muted" className="mt-2">
                  {tAddresses("coordinates")}:{" "}
                  {selectedAddress.latitude.toFixed(6)},{" "}
                  {selectedAddress.longitude.toFixed(6)}
                </Text>
                {/* External map links */}
                <HStack wrap className="mt-4">
                  <Button
                    color="neutral"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      window.open(
                        `https://www.google.com/maps?q=${selectedAddress.latitude},${selectedAddress.longitude}`,
                        "_blank",
                      )
                    }
                  >
                    {tAddresses("openInGoogleMaps")}
                  </Button>
                  <Button
                    color="neutral"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      window.open(
                        `https://yandex.com/maps/?pt=${selectedAddress.longitude},${selectedAddress.latitude}&z=15&l=map`,
                        "_blank",
                      )
                    }
                  >
                    {tAddresses("openInYandexMaps")}
                  </Button>
                  <Button
                    color="neutral"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const locationUrl = `https://www.google.com/maps?q=${selectedAddress.latitude},${selectedAddress.longitude}`;
                      const text = `${selectedAddress.name}\n${selectedAddress.address}`;
                      window.open(
                        `https://t.me/share/url?url=${encodeURIComponent(locationUrl)}&text=${encodeURIComponent(text)}`,
                        "_blank",
                      );
                    }}
                  >
                    {tAddresses("shareInTelegram")}
                  </Button>
                </HStack>
              </Surface>
            </ModalBody>
            <ModalFooter>
              <Button color="neutral" variant="outline" onClick={closeModal}>
                {tCommon("close")}
              </Button>
              <Button
                onClick={() => {
                  if (selectedAddress)
                    openDialog("address", selectedAddress.id);
                }}
              >
                <Icon.Edit size="sm" />
                {tCommon("edit")}
              </Button>
            </ModalFooter>
          </>
        )}
      </AdaptiveSheet>

      <ConfirmDialog
        open={showDeleteConfirm}
        title={tCommon("confirmDelete")}
        description={tCommon("confirmDeleteDescription")}
        confirmLabel={tCommon("delete")}
        cancelLabel={tCommon("cancel")}
        confirmColor="danger"
        loading={deleteLoading}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      <AddressModal
        open={dialog?.type === "address"}
        onClose={closeModal}
        address={selectedAddress ?? undefined}
        onSaved={() => {
          invalidateAddresses();
          closeModal();
        }}
        mapHeightClassName="h-[380px] min-h-[300px]"
        size="2xl"
        shipperParty={selectedBrokerShipper ?? null}
        shipperPartyEditable={Boolean(isBroker && !selectedAddress)}
        shipperPartyOptions={brokerShippers.map((shipper) => ({
          value: shipper.id,
          label: shipper.name,
        }))}
        onShipperPartyChange={setSelectedShipperId}
        shipperPartyDisabled={isShipperLocked}
      />

      {isCompact && (
        <Fab
          onClick={handleAddNew}
          aria-label={tAddresses("addAddress")}
          data-testid="addresses-fab"
        />
      )}
    </VStack>
  );
}

export const Route = createFileRoute("/_app/addresses")({
  component: AddressesPage,
});
